import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import matomoService from '../services/matomoService';

interface MatomoTrackerProps {
  matomoUrl: string;
  siteId: number;
  enableLinkTracking?: boolean;
  trackPageView?: boolean;
  userId?: string;
}

/**
 * MatomoTracker component that initializes Matomo and tracks page views
 * 
 * This component should be placed near the root of your application
 * to ensure Matomo is initialized and page views are tracked correctly.
 */
const MatomoTracker: React.FC<MatomoTrackerProps> = ({
  matomoUrl,
  siteId,
  enableLinkTracking = true,
  trackPageView = true,
  userId
}) => {
  const location = useLocation();

  // Initialize Matomo once when the component mounts
  useEffect(() => {
    matomoService.init(matomoUrl, siteId);
    
    if (userId) {
      matomoService.setUserId(userId);
    }
  }, [matomoUrl, siteId, userId]);

  // Track page view on route changes
  useEffect(() => {
    if (trackPageView) {
      // Add a small delay to ensure the page has fully loaded
      const timeoutId = setTimeout(() => {
        matomoService.trackPageView();
      }, 100);
      
      return () => clearTimeout(timeoutId);
    }
  }, [location, trackPageView]);

  // This component doesn't render anything
  return null;
};

export default MatomoTracker;
